import React, { Component, Fragment } from 'react';
import {
  LoginActions,
  QueryParameterNames,
} from '../api-authorization/ApiAuthorizationConstants';
import authService, {
  AuthenticationResultStatus,
} from '../api-authorization/AuthorizeService';

// Types for action prop from the known LoginActions literal map
type LoginAction = (typeof LoginActions)[keyof typeof LoginActions];

interface LoginProcessProps {
  action: LoginAction;
}

interface LoginProcessState {
  message?: string;
}

// The main responsibility of this component is to handle the user's login process.
// This is the starting point for the login process. Any component that needs to authenticate
// a user can simply perform a redirect to this component with a returnUrl query parameter and
// let the component perform the login and return back to the return url.
export class LoginProcess extends Component<
  LoginProcessProps,
  LoginProcessState
> {
  constructor(props: LoginProcessProps) {
    super(props);
    this.state = {
      message: undefined,
    };
  }

  componentDidMount(): void {
    const action = this.props.action;
    switch (action) {
      case LoginActions.Login:
        void this.login(this.getReturnUrl());
        break;
      case LoginActions.LoginCallback:
        void this.processLoginCallback();
        break;
      case LoginActions.LoginFailed:
        // If you want to surface the error message from query string, uncomment:
        // const params = new URLSearchParams(window.location.search);
        // const error = params.get(QueryParameterNames.Message) ?? undefined;
        // this.setState({ message: error });
        break;
      default:
        throw new Error(`Invalid action '${action}'`);
    }
  }

  render(): React.ReactNode {
    const action = this.props.action;
    const { message } = this.state;

    if (message) {
      return <div className="container-authorization-service">{message}</div>;
    } else {
      switch (action) {
        case LoginActions.Login:
          return (
            <Fragment>
              <div className="container-authorization-service">
                Processing login
              </div>
              {/* <Spin /> */}
            </Fragment>
          );
        case LoginActions.LoginCallback:
          return (
            <Fragment>
              <div className="container-authorization-service">
                Processing login callback
              </div>
              {/* <Spin /> */}
            </Fragment>
          );
        case LoginActions.Profile:
        case LoginActions.Register:
          return <div className="container-authorization-service" />;
        default:
          throw new Error(`Invalid action '${action}'`);
      }
    }
  }

  private async login(returnUrl: string): Promise<void> {
    const state = { returnUrl };
    const result = await authService.signIn(state);
    switch (result.status) {
      case AuthenticationResultStatus.Redirect:
        break;
      case AuthenticationResultStatus.Success:
        await this.navigateToReturnUrl(returnUrl);
        break;
      case AuthenticationResultStatus.Fail:
        this.setState({ message: result.message });
        break;
      default:
        throw new Error(`Invalid status result ${String(result.status)}.`);
    }
  }

  private async processLoginCallback(): Promise<void> {
    const url = window.location.href;
    const result = await authService.completeSignIn(url);

    switch (result.status) {
      case AuthenticationResultStatus.Redirect:
        // There should not be any redirects as the only time completeSignIn finishes
        // is when we are doing a redirect sign in flow.
        throw new Error('Should not redirect.');
      case AuthenticationResultStatus.Success:
        await this.navigateToReturnUrl(
          this.getReturnUrl(result.state as { returnUrl?: string } | undefined),
        );
        break;
      case AuthenticationResultStatus.Fail:
        this.setState({ message: result.message });
        break;
      default:
        throw new Error(
          `Invalid authentication result status '${String(result.status)}'.`,
        );
    }
  }

  private getReturnUrl(state?: { returnUrl?: string }): string {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get(QueryParameterNames.ReturnUrl);
    if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
      // This is an extra check to prevent open redirects.
      throw new Error(
        'Invalid return url. The return url needs to have the same origin as the current page.',
      );
    }
    return state?.returnUrl || fromQuery || `${window.location.origin}/app`;
  }

  private redirectToApiAuthorizationPath(apiAuthorizationPath: string): void {
    const redirectUrl = `${window.location.origin}${apiAuthorizationPath}`;
    // It's important that we do a replace here so that when the user hits the back arrow on the
    // browser he gets sent back to where it was on the app instead of to an endpoint on this
    // component.
    window.location.replace(redirectUrl);
  }

  private async navigateToReturnUrl(returnUrl: string): Promise<void> {
    // It's important that we do a replace here so that we remove the callback uri with the
    // fragment containing the tokens from the browser history.
    window.location.replace(returnUrl);
  }
}
